(ns ui.animation-editor.stage
  "Drives the CreateJS composition inside the iframe of the editor
   (`/story-template/templates.html`, exported by Adobe Animate): applies
   the stage model of `ui.animation-editor.model`, loads the background
   image, changes the background color and plays the animation from the
   start. Same JS interop as `storrito.instagram.story.renderer.core`,
   which stays for the recorder page (`/story-renderer.html`). Every
   function is a no-op while the iframe is not there or not loaded yet."
  (:require [goog.object :as obj]
            [ui.animation-editor.model :as model]))

(def iframe-id
  "animation-editor-stage")

(def template-path
  "/story-template/templates.html")

(defn ^js content-window
  []
  (some-> (js/document.getElementById iframe-id)
          (obj/get "contentWindow")))

(defn ^js export-root
  "The root movie clip of the composition, set by the template page once
   the manifest is loaded."
  []
  (some-> (content-window)
          (obj/get "exportRoot")))

(defn ^js stage
  []
  (some-> (export-root)
          (obj/get "stage")))

(defn obj-merge!
  "Merges the nested `properties` map into the JS `object`: a map value
   recurses into the property of that name."
  [^js object properties]
  (when object
    (doseq [[k v] properties]
      (if (map? v)
        (obj-merge! (obj/get object (name k))
                    v)
        (obj/set object (name k) v))))
  object)

(defn init!
  "Starts loading the composition inside the iframe (`init` of the
   template page). Calls `ready` with the function that starts the
   ticker of the animation, once the manifest is loaded. Returns false
   when the page did not load (the iframe shows something else then)."
  [ready]
  (let [^js w (content-window)]
    (if (fn? (some-> w (obj/get "init")))
      (do (.init w ready true)
          true)
      false)))

(defn set-attributes!
  "Applies texts, fonts, presets and the background image of the
   `entity` to the composition."
  [entity]
  (when-let [root (export-root)]
    (obj-merge! root
                (model/stage-model entity))))

(defn load-background-image!
  "Sets the background image of the `entity` on its background symbol
   and calls `loaded` once the browser has the image (or failed to load
   it), or right away when the template has no image yet."
  [entity loaded]
  (let [url (model/background-image-url entity)
        symbol (some-> (export-root)
                       (obj/get (model/symbol-name entity :background)))]
    (if (and url symbol)
      (obj-merge! symbol
                  {:instance {:image {:crossOrigin "anonymous"
                                      :onload loaded
                                      :onerror loaded
                                      :src url}}
                   :visible true})
      (loaded))))

(defn prepare!
  "Applies the `entity` and loads its background image, then updates the
   stage and calls `ready`."
  [entity ready]
  (set-attributes! entity)
  (load-background-image! entity
                          (fn []
                            (when-let [^js s (stage)]
                              (.update s))
                            (ready))))

(defn restart!
  "Plays the animation from its first frame."
  []
  ;; The `^js` hint has to sit on the local the method is called on:
  ;; through `some->` the CreateJS method names were not inferred as
  ;; externs and the release build renamed `getChildAt`
  ;; (STORRITO-V2-K5, "a.wl is not a function").
  (when-let [^js s (stage)]
    (when-let [^js root (.getChildAt s 0)]
      (.gotoAndPlay root 1))))

(defn set-background-color!
  "Changes the fill color of the `BG` shape of the composition."
  [color]
  (when-let [fill (some-> (export-root)
                          (obj/getValueByKeys "BG" "children" 0 "graphics" "_fill"))]
    (obj/set fill "style" color)))
